import { useMemo } from 'react';
import { clsx } from 'clsx';

import { CircularProgress as MuiCircularProgress, css, styled } from '@mui/material';
import { capitalizeFirstLetter } from '@kc-console/core';

import { calculateProgressValue, MAX_PROGRESS_VALUE, MIN_PROGRESS_VALUE } from '../../utils';
import { circleProgressClasses } from './theme';
import { CircleProgressSizeMap, CircleProgressThicknessMap } from './types';

import type { CircleProgressOwnerState, CircleProgressProps } from './types';

const CircleProgress = (props: CircleProgressProps) => {
  // 마이그레이션된 패턴: 기본값을 컴포넌트에서 직접 처리
  const {
    min = MIN_PROGRESS_VALUE,
    max = MAX_PROGRESS_VALUE,
    clockwise = true,
    size = 'max',
    showPercentage,
    value = 0,
    className,
    thickness,
    color = 'default',
    ...others
  } = props;

  const ownerState: CircleProgressOwnerState = {
    ...props,
    size,
    color,
  };

  // 동적 클래스 생성 (useUtilityClasses 대체)
  const getDynamicClasses = () => {
    return {
      root: clsx(
        // circleProgressClasses.root는 target으로 자동 적용되므로 제외
        size && circleProgressClasses[`size${capitalizeFirstLetter(size)}`],
        color && circleProgressClasses[`color${capitalizeFirstLetter(color)}`],
      ),
    };
  };

  const dynamicClasses = getDynamicClasses();

  const refinedValue = useMemo(() => {
    return calculateProgressValue({ value, min, max });
  }, [max, min, value]);

  const progressValue = useMemo(() => {
    if (!clockwise) return refinedValue * -1;

    return refinedValue;
  }, [clockwise, refinedValue]);

  return (
    <CircleProgressRoot ownerState={ownerState} className={clsx(dynamicClasses.root, className)}>
      <CircleProgressBase
        {...others}
        size={CircleProgressSizeMap[size]}
        variant="determinate"
        value={100}
        thickness={thickness ?? CircleProgressThicknessMap[size]}
        ownerState={ownerState}
        disableShrink
      />
      <CircleProgressCircle
        {...others}
        size={CircleProgressSizeMap[size]}
        variant="determinate"
        value={progressValue}
        thickness={thickness ?? CircleProgressThicknessMap[size]}
        ownerState={ownerState}
        disableShrink
      />
      {size === 'max' && showPercentage && (
        <CircleProgressPercentage
          ownerState={ownerState}
        >{`${Math.round(refinedValue)}%`}</CircleProgressPercentage>
      )}
    </CircleProgressRoot>
  );
};

CircleProgress.displayName = 'CircleProgress';

export default CircleProgress;

// Styled Components - 하이브리드 패턴
const CircleProgressRoot = styled('div', {
  target: circleProgressClasses.root,
})<{ ownerState: CircleProgressOwnerState }>(({ ownerState }) => {
  const sizeStyles = getSizeStyles(ownerState.size);
  const colorStyles = getColorStyles(ownerState.color);

  return css`
    position: relative;
    ${sizeStyles}
    ${colorStyles}
  `;
});

const CircleProgressBase = styled(MuiCircularProgress, {
  target: circleProgressClasses.bgCircle,
})<{ ownerState: CircleProgressOwnerState }>`
  position: absolute;
`;

const CircleProgressCircle = styled(MuiCircularProgress, {
  target: circleProgressClasses.circle,
})<{ ownerState: CircleProgressOwnerState }>`
  position: absolute;
`;

const CircleProgressPercentage = styled('div', {
  target: circleProgressClasses.percentage,
})<{ ownerState: CircleProgressOwnerState }>`
  width: inherit;
  height: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  font-size: 18px;
  font-weight: 700;
  line-height: 27px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
`;

// 스타일 헬퍼 함수들
const getSizeStyles = (size: CircleProgressOwnerState['size']) => {
  const sizeStylesMap = {
    xs: css`
      width: ${CircleProgressSizeMap['xs']}px;
      height: ${CircleProgressSizeMap['xs']}px;
    `,
    sm: css`
      width: ${CircleProgressSizeMap['sm']}px;
      height: ${CircleProgressSizeMap['sm']}px;
    `,
    md: css`
      width: ${CircleProgressSizeMap['md']}px;
      height: ${CircleProgressSizeMap['md']}px;
    `,
    lg: css`
      width: ${CircleProgressSizeMap['lg']}px;
      height: ${CircleProgressSizeMap['lg']}px;
    `,
    max: css`
      width: inherit;
      height: inherit;
    `,
  };

  return sizeStylesMap[size ?? 'max'] || css``;
};

const getColorStyles = (color: CircleProgressOwnerState['color']) => {
  const colorStylesMap = {
    default: css`
      & .${circleProgressClasses.circle} {
        color: #323841;
      }

      & .${circleProgressClasses.bgCircle} {
        color: #e3e7ed;
      }

      & .${circleProgressClasses.percentage} {
        color: #676f7d;
      }
    `,
    inverse: css`
      & .${circleProgressClasses.circle} {
        color: #e4ebff;
      }

      & .${circleProgressClasses.bgCircle} {
        color: #ffffff26;
      }

      & .${circleProgressClasses.percentage} {
        color: #f9fafb;
      }
    `,
  };

  return colorStylesMap[color ?? 'default'] || css``;
};
